import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import type { CustomerPickupQrDetail } from '../ordersApi.js';

export interface PickupQrCodeProps {
  readonly detail: CustomerPickupQrDetail;
}

/** Renders pickup QR / verify link for READY orders (Journey C/K/A). */
export function PickupQrCode({ detail }: PickupQrCodeProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const payload = detail.verifyUrl ?? detail.token;

  const qrPayload =
    detail.visible && payload !== null && payload.length > 0 ? payload : null;

  useEffect(() => {
    if (qrPayload === null) {
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(qrPayload, {
      margin: 1,
      width: 220,
      errorCorrectionLevel: 'M',
    }).then((url) => {
      if (!cancelled) {
        setDataUrl(url);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [qrPayload]);

  if (!detail.visible) {
    return null;
  }

  const showQrImage = dataUrl !== null && qrPayload !== null;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-[var(--color-border)] p-3">
      <p className="text-sm font-medium text-[var(--color-on-surface)]">
        {t('account.orders.pickupQrTitle')}
      </p>
      {showQrImage ? (
        <img
          src={dataUrl}
          alt={t('account.orders.pickupQrTitle')}
          className="mx-auto h-[220px] w-[220px] rounded-md border border-[var(--color-border)] bg-white p-2"
        />
      ) : null}
      {detail.verifyUrl ? (
        <a
          className="text-xs text-[var(--color-accent)] underline break-all"
          href={detail.verifyUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          {detail.verifyUrl}
        </a>
      ) : null}
      {detail.token ? (
        <p className="text-xs text-[var(--color-on-surface-muted)] break-all font-mono">
          {detail.token}
        </p>
      ) : null}
    </div>
  );
}
